/**
 * Public customer satisfaction survey — Georgian only, SmartPaw branded.
 */
import React, { useState } from 'react';
import { PawPrint, Star, Loader2, CheckCircle2, AlertCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL || '';

const LOVED_OPTIONS = [
  { value: 'service_quality_comfort', label: 'მომსახურების ხარისხი და კომფორტულობა' },
  { value: 'attentive_trusted_team',  label: 'ყურადღებიანი და სანდო გუნდი' },
  { value: 'smart_dispenser',         label: 'ჭკვიანი დისპენსერი' },
  { value: 'prices',                  label: 'პროდუქტების ფასები' },
  { value: 'product_variety',         label: 'პროდუქტების მრავალფეროვნება' },
];

function StarRating({ value, onChange, testId }) {
  return (
    <div className="flex gap-1.5" data-testid={testId} role="radiogroup">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          aria-label={`${n} stars`}
          role="radio"
          aria-checked={value === n}
          className="transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F25C05]/40 rounded-full"
          data-testid={`${testId}-${n}`}
        >
          <Star
            className={`h-9 w-9 ${n <= value ? 'fill-[#F25C05] text-[#F25C05]' : 'text-[#0A4D8C33]'}`}
            strokeWidth={1.5}
          />
        </button>
      ))}
    </div>
  );
}

function NPSScale({ value, onChange, testId }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testId} role="radiogroup">
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          role="radio"
          aria-checked={value === n}
          className={`h-11 w-11 rounded-xl font-bold transition-all tabular-nums ${
            value === n
              ? 'bg-[#F25C05] text-white shadow-md scale-105'
              : 'bg-white text-[#05223D] border border-[#0A4D8C26] hover:border-[#F25C05] hover:text-[#F25C05]'
          }`}
          data-testid={`${testId}-${n}`}
        >
          {n}
        </button>
      ))}
    </div>
  );
}

export default function SurveyPage() {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [overall, setOverall] = useState(0);
  const [overallComment, setOverallComment] = useState('');
  const [delivery, setDelivery] = useState(0);
  const [loved, setLoved] = useState([]);
  const [improvements, setImprovements] = useState('');
  const [futureWishes, setFutureWishes] = useState('');
  const [recommend, setRecommend] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const toggleLoved = (v) =>
    setLoved((cur) => (cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!overall || !delivery || !recommend) {
      return setError('გთხოვ შეავსე შეფასების ველები (ვარსკვლავები და რეკომენდაცია).');
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/survey/satisfaction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim() || null,
          phone: phone.trim() || null,
          overall_rating: overall,
          overall_comment: overallComment.trim() || null,
          delivery_rating: delivery,
          loved_most: loved,
          improvements: improvements.trim() || null,
          future_wishes: futureWishes.trim() || null,
          recommend_score: recommend,
        }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || `HTTP ${r.status}`);
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.message || 'შეცდომა გადაცემისას');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="border-b border-[#0A4D8C14] bg-white">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 group" data-testid="survey-logo">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#F25C05] text-white group-hover:bg-[#d44e02] transition-colors">
              <PawPrint className="h-4 w-4" />
            </span>
            <span className="font-display font-bold text-[#05223D] text-lg tracking-tight">SmartPaw</span>
          </Link>
          <span className="text-xs uppercase tracking-[0.22em] font-bold text-[#465B70]">გამოკითხვა</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 md:px-8 py-10 md:py-14">
        {done ? (
          <div
            className="rounded-3xl border border-[#A9B89A]/40 bg-white p-10 md:p-14 text-center shadow-[0_18px_50px_-30px_rgba(10,77,140,0.25)]"
            data-testid="survey-success"
          >
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#A9B89A]/40 text-[#0A4D8C]">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <h2 className="mt-5 font-display text-2xl md:text-3xl font-bold text-[#05223D]">
              გმადლობთ თქვენი დროისთვის! 🐾
            </h2>
            <p className="mt-4 text-[#465B70] leading-relaxed max-w-md mx-auto">
              თქვენი გამოხმაურება ძალიან მნიშვნელოვანია ჩვენთვის. ჩვენ მუდმივად ვცდილობთ
              SmartPaw უკეთესი გავხადოთ თქვენთვის და თქვენი ცხოველებისთვის.
            </p>
            <Link
              to="/"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#F25C05] px-6 py-3 font-bold text-white hover:bg-[#d44e02] transition-all"
            >
              დაბრუნდი მთავარზე
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        ) : (
          <>
            <div className="text-center mb-8 md:mb-12">
              <p className="text-xs tracking-[0.24em] uppercase font-bold text-[#F25C05]">
                მომხმარებელთა კმაყოფილების გამოკითხვა
              </p>
              <h1 className="mt-3 font-display font-bold text-[#05223D] text-3xl md:text-4xl tracking-tight">
                გაგვიზიარეთ თქვენი აზრი!
              </h1>
              <p className="mt-4 text-[#465B70] leading-relaxed max-w-2xl mx-auto">
                SmartPaw-ში ჩვენ მუდმივად ვმუშაობთ ჩვენი სერვისების გაუმჯობესებაზე — თქვენთვის
                და თქვენი შინაური ცხოველებისთვის. ეს მოკლე კითხვარი 5 წუთზე ნაკლებს წაგართმევთ,
                ხოლო ჩვენ დაგვეხმარება, რომ მომსახურება უფრო მორგებული და ხარისხიანი გავხადოთ.
              </p>
              <p className="mt-3 text-sm text-[#465B70]">
                გმადლობთ, რომ SmartPaw-ის ოჯახის ნაწილი ხართ! 🐾
              </p>
            </div>

            <form onSubmit={onSubmit} noValidate className="space-y-6 md:space-y-7">
              <Card>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="s-name">თქვენი სახელი, გვარი</Label>
                    <Input id="s-name" value={name} onChange={(e) => setName(e.target.value)} className="h-11 rounded-xl" data-testid="survey-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="s-phone">თქვენი საკონტაქტო ნომერი</Label>
                    <Input id="s-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+995 5__ __ __ __" className="h-11 rounded-xl" data-testid="survey-phone" />
                  </div>
                </div>
              </Card>

              <Card>
                <QHeader n="1">რამდენად კმაყოფილი ხართ SmartPaw-ის სერვისით?</QHeader>
                <StarRating value={overall} onChange={setOverall} testId="survey-overall" />
                <Textarea
                  value={overallComment}
                  onChange={(e) => setOverallComment(e.target.value)}
                  placeholder="დაამატე კომენტარი (არასავალდებულო)"
                  rows={3}
                  className="mt-4 rounded-xl"
                  data-testid="survey-overall-comment"
                />
              </Card>

              <Card>
                <QHeader n="2">როგორ შეაფასებდით მოწოდების სიჩქარეს და სანდოობას?</QHeader>
                <StarRating value={delivery} onChange={setDelivery} testId="survey-delivery" />
              </Card>

              <Card>
                <QHeader n="3">რა მოგწონთ SmartPaw-ში ყველაზე მეტად?</QHeader>
                <div className="space-y-2.5">
                  {LOVED_OPTIONS.map((o) => (
                    <label
                      key={o.value}
                      className="flex items-start gap-3 rounded-xl border border-[#0A4D8C1A] bg-white px-3.5 py-2.5 cursor-pointer hover:border-[#F25C05]/40 transition-all"
                      data-testid={`survey-loved-${o.value}`}
                    >
                      <Checkbox
                        checked={loved.includes(o.value)}
                        onCheckedChange={() => toggleLoved(o.value)}
                      />
                      <span className="text-sm md:text-[15px] text-[#05223D] leading-snug">{o.label}</span>
                    </label>
                  ))}
                </div>
              </Card>

              <Card>
                <QHeader n="4">რას შეცვლიდით / რის გაუმჯობესებას ისურვებდით SmartPaw-ის მომსახურებაში?</QHeader>
                <Textarea
                  value={improvements}
                  onChange={(e) => setImprovements(e.target.value)}
                  rows={4}
                  placeholder="დაგვიწერე..."
                  className="rounded-xl"
                  data-testid="survey-improvements"
                />
              </Card>

              <Card>
                <QHeader n="5">რა ახალ პროდუქტს / მომსახურებას ისურვებდით მომავალში SmartPaw-ში?</QHeader>
                <Textarea
                  value={futureWishes}
                  onChange={(e) => setFutureWishes(e.target.value)}
                  rows={4}
                  placeholder="დაგვიწერე..."
                  className="rounded-xl"
                  data-testid="survey-future"
                />
              </Card>

              <Card>
                <QHeader n="6">რამდენად ურჩევდით SmartPaw-ის მომსახურებას მეგობარს ან ოჯახის წევრს?</QHeader>
                <p className="text-xs text-[#465B70] mb-3">1 = სრულებით არ ვურჩევდი &nbsp;·&nbsp; 10 = აუცილებლად ვურჩევდი</p>
                <NPSScale value={recommend} onChange={setRecommend} testId="survey-recommend" />
              </Card>

              {error && (
                <div className="flex items-start gap-2 rounded-xl border border-[#9b1c1c]/30 bg-[#9b1c1c]/8 px-4 py-3 text-sm text-[#9b1c1c]" data-testid="survey-error">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="rounded-full bg-[#F25C05] hover:bg-[#d44e02] px-7 py-6 text-base font-bold"
                  data-testid="survey-submit"
                >
                  {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  გაგზავნა
                </Button>
              </div>
            </form>
          </>
        )}
      </main>

      <footer className="text-center py-8 text-xs text-[#465B70]">
        SmartPaw &nbsp;|&nbsp; smartpaw.ge
      </footer>
    </div>
  );
}

function Card({ children }) {
  return (
    <div className="rounded-2xl border border-[#0A4D8C14] bg-white p-5 md:p-7 shadow-[0_4px_18px_-12px_rgba(10,77,140,0.18)]">
      {children}
    </div>
  );
}

function QHeader({ n, children }) {
  return (
    <div className="mb-4">
      <span className="inline-block text-[10px] uppercase tracking-[0.2em] font-bold text-[#F25C05] mb-2">
        კითხვა {n}
      </span>
      <p className="font-display font-bold text-[#05223D] text-lg md:text-xl tracking-tight leading-snug">
        {children}
      </p>
    </div>
  );
}
